// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import {
    getProductListDataViewProperty,
    isLoadMore,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { sortSelectStyles } from "@insite/content-library/Widgets/ProductList/ProductListSortSelect";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { plpSelectStyle } from "@rugby/App/WidgetExtensions/ProductList/ProductListSortSelectExtensions";
import Pagination, { PaginationPresentationProps } from "@rugby/MobiusOverrides/Pagination";
import { screen } from "@rugby/XDL/Helpers";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
const enum fields {
    showPagesNumber = "showPageNumber",
    showResultsPerPage = "showResultsPerPage",
    paginationAlignment = "paginationAlignment",
}

interface FieldsProps {
    fields: {
        [fields.showPagesNumber]: boolean;
        [fields.showResultsPerPage]: boolean;
        [fields.paginationAlignment]: "left" | "center" | "right";
    };
}
// </XNG-Changes>

const mapStateToProps = (state: ApplicationState) => ({
    hidePagination: isLoadMore(state),
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

// <XNG-Changes>
type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & FieldsProps;
// </XNG-Changes>

export interface ProductListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const paginationStyles: ProductListPaginationStyles = {};

// <XNG-Changes>
export const customPaginationStyles: ProductListPaginationStyles = {
    ...paginationStyles,
    pagination: {
        cssOverrides: {
            pagination: css`
                ${({ theme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin: 0;
                            `,
                            null,
                            null,
                            css`
                                margin: 0;
                            `,
                            null,
                        ],
                        "min",
                    )};
            `,
            perPageSelect: css`
                ${screen("lte", "sm")`
                    justify-content: flex-start;                            
                `}
            `,
        },
        selectProps: {
            ...plpSelectStyle,
            labelProps: {
                css: css`
                    width: unset;
                    // we need it to make label width equal "sort" label width
                    ${screen("lte", "sm")`
                        min-width: 60px;                            
                    `}
                `,
            },
            cssOverrides: {
                ...sortSelectStyles.select!.cssOverrides,
                formField: css`
                    width: 190px;
                    // we need it to make form width equal "sort" form width
                    ${screen("lte", "sm")`
                        min-width: 210px;                            
                    `}
                `,
            },
        },
    },
};

const styles = customPaginationStyles;
// </XNG-Changes>

const ProductListPagination = ({ addProductFilters, hidePagination, pagination, fields }: Props) => {
    const [shouldScrollToTop, setShouldScrollToTop] = useState(false);
    useEffect(() => {
        if (shouldScrollToTop) {
            window.scrollTo(0, 0);
            setShouldScrollToTop(false);
        }
    }, [pagination?.page]);
    const changePage = (newPageIndex: number) => {
        addProductFilters({ page: newPageIndex });
        setShouldScrollToTop(true);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        addProductFilters({ pageSize: newPageSize });
    };

    if (hidePagination || !pagination || !pagination.totalItemCount) {
        return null;
    }

    // <XNG-Changes>
    const showPageNumber = fields.showPageNumber === undefined ? true : fields.showPageNumber;
    const showResultsPerPage = fields.showResultsPerPage === undefined ? true : fields.showResultsPerPage;
    // </XNG-Changes>

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageLabel="Show"
            resultsPerPageOptions={pagination.pageSizeOptions}
            showPagesNumber={showPageNumber}
            showResultsPerPage={showResultsPerPage}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            pageSizeCookie="ProductList-PageSize"
            data-test-selector="productList_pagination"
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
        />
    );
};

// <XNG-Changes> add fieldDefinitions
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Pagination",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showPagesNumber,
                displayName: "Show Pages Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showResultsPerPage,
                displayName: "Show Result per page",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.paginationAlignment,
                editorTemplate: "DropDownField",
                defaultValue: "center",
                fieldType: "General",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                hideEmptyOption: true,
            },
        ],
    },
};

export default widgetModule;
